"use client";

import { Award } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";

export type CourseCertItem = { title: string; issuer?: string; note?: string; image?: string };

function CertCard({ item: c }: { item: CourseCertItem }) {
  return (
    <div className="h-full rounded-[var(--radius-card)] border border-line bg-white p-5 shadow-soft">
      {c.image ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={c.image} alt="" loading="lazy" className="h-10 w-10 rounded-xl border border-line bg-white object-cover" />
      ) : (
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gold-500/15 text-gold-700">
          <Award className="h-5 w-5" />
        </span>
      )}
      {c.issuer && <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-700">{c.issuer}</p>}
      <h3 className={`${c.issuer ? "mt-1" : "mt-3"} font-display text-[15px] font-semibold text-ink`}>{c.title}</h3>
      {c.note && <p className="mt-1.5 text-[13px] leading-relaxed text-ink-soft">{c.note}</p>}
    </div>
  );
}

/** Continuously gliding, looping certification cards for a course page; pauses on hover. */
export function CourseCertSwiper({ items }: { items: CourseCertItem[] }) {
  // Fewer than 3 certifications: show them statically, no looping.
  if (items.length < 3) {
    return (
      <div className="mt-5 flex flex-wrap gap-4">
        {items.map((c, i) => (
          <div key={i} className="w-[18rem] sm:w-[21rem]">
            <CertCard item={c} />
          </div>
        ))}
      </div>
    );
  }
  // Swiper's loop needs more slides than fit on screen, so repeat short lists.
  const slides = items.length < 8 ? Array.from({ length: Math.ceil(8 / items.length) }, () => items).flat() : items;
  return (
    <Swiper
      modules={[Autoplay]}
      loop
      spaceBetween={16}
      slidesPerView="auto"
      speed={8000}
      allowTouchMove={false}
      autoplay={{ delay: 0, disableOnInteraction: false, pauseOnMouseEnter: true }}
      className="mt-5 pb-2! [&_.swiper-wrapper]:ease-linear!"
    >
      {slides.map((c, i) => (
        <SwiperSlide key={i} className="h-auto! w-[18rem]! sm:w-[21rem]!">
          <CertCard item={c} />
        </SwiperSlide>
      ))}
    </Swiper>
  );
}
